import { DestroyRef, inject, Injectable, NgZone } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'

import posthog from 'posthog-js'
import { filter } from 'rxjs'

import { POSTHOG_EVENTS } from './posthog-events'
import { ENVIRONMENT } from '../tokens/environment.token'

@Injectable({ providedIn: 'root' })
/**
 * Service for integrating and managing PostHog analytics in an Angular application.
 *
 * This service initializes PostHog with the provided environment configuration
 * and sets up automatic pageview tracking on Angular route navigation events.
 * It also provides a method to track custom events with optional properties.
 *
 * @remarks
 * - The service runs PostHog initialization outside of Angular's zone to avoid
 *   unnecessary change detection cycles.
 * - Pageview tracking is triggered on `NavigationEnd` events.
 * - Ensure that the `ENVIRONMENT` injection token is properly configured with
 *   `production`, `posthog.apiKey`, and `posthog.apiHost` values.
 *
 * @example
 * ```typescript
 * // Example usage in a component or service
 * constructor(private posthogService: PosthogService) {}
 *
 * this.posthogService.track('event_name', { key: 'value' });
 * ```
 */
export class PosthogService {
  private readonly env = inject(ENVIRONMENT)

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.initPostHog()
  }

  private initPostHog() {
    this.ngZone.runOutsideAngular(() => {
      if (this.env.production) {
        posthog.init(this.env.posthog.apiKey, {
          api_host: this.env.posthog.apiHost,
          debug: false,
          cross_subdomain_cookie: true,
          person_profiles: 'always',
          capture_pageview: false,
          capture_pageleave: true,
          loaded: (posthogInstance) => {
            this.router.events
              .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
              )
              .subscribe((event: NavigationEnd) => {
                posthogInstance.capture('$pageview')
              })
          }
        })
      }
    })
  }

  track(event: keyof typeof POSTHOG_EVENTS, properties?: Record<string, unknown>) {
    posthog.capture(POSTHOG_EVENTS[event], properties)
  }
}
